import React, { FC, ReactNode, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Dialog } from 'antd-mobile';

import { useNavbar } from '../../../hooks/use-navbar';
import { useLivretRead } from '../../../hooks/use-livret-read';
import { faroLogger } from '../../../observability/faro-logger';

const ReadConfirm: FC<{ sectionKey: string; sectionTitle: string }> = ({
  sectionKey,
  sectionTitle,
}) => {
  const { isRead, readAt, markAsRead } = useLivretRead(`consignes/${sectionKey}`);
  const navigate = useNavigate();
  const handleConfirm = (): void => {
    markAsRead();
    faroLogger.info('livret_section_read_confirmed', {
      section: `consignes/${sectionKey}`,
      title: sectionTitle,
    });
    navigate('/livret');
  };
  return (
    <div
      style={{
        margin: '24px 0 12px',
        padding: '14px',
        background: isRead
          ? 'rgba(16,185,129,0.10)'
          : 'rgba(49,220,200,0.06)',
        border: isRead
          ? '1px solid rgba(16,185,129,0.4)'
          : '1px solid rgba(49,220,200,0.4)',
        borderRadius: 14,
      }}
    >
      {isRead && readAt && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: '#10B981',
            marginBottom: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          ✓ Lu le{' '}
          {new Date(readAt).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          })}{' '}
          à{' '}
          {new Date(readAt).toLocaleTimeString('fr-FR', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </div>
      )}
      <button
        type="button"
        onClick={handleConfirm}
        style={{
          appearance: 'none',
          background: isRead
            ? 'var(--bg-card)'
            : 'linear-gradient(135deg, #31DCC8, #0FBDAD)',
          border: isRead
            ? '1px solid var(--border-strong)'
            : 'none',
          borderRadius: 12,
          padding: '14px 16px',
          color: isRead ? 'var(--text-primary)' : '#0B1628',
          fontFamily: 'inherit',
          fontSize: 16,
          fontWeight: 800,
          cursor: 'pointer',
          width: '100%',
          boxShadow: isRead ? 'none' : '0 6px 20px rgba(49,220,200,0.32)',
          letterSpacing: '-0.005em',
          WebkitTapHighlightColor: 'transparent',
        }}
      >
        {isRead
          ? '↩ Retour au sommaire'
          : '✓ J’ai lu — retour au sommaire'}
      </button>
    </div>
  );
};

/* ============================================================ */
/* Theme & primitives                                            */
/* ============================================================ */

const C = {
  teal: '#31DCC8',
  navy: 'var(--navy)',
  white: 'var(--text-primary)',
  muted: 'var(--text-muted)',
  warn: '#F59E0B',
  warnBg: 'rgba(245,158,11,0.10)',
  warnBorder: 'rgba(245,158,11,0.45)',
  danger: '#FF8A95',
  dangerStrong: '#E63946',
  dangerBg: 'rgba(230,57,70,0.10)',
  dangerBorder: 'rgba(230,57,70,0.45)',
  ok: '#10B981',
  okBg: 'rgba(16,185,129,0.10)',
  okBorder: 'rgba(16,185,129,0.4)',
  info: '#3B82F6',
  infoBg: 'rgba(59,130,246,0.10)',
  infoBorder: 'rgba(59,130,246,0.4)',
};

const SecT: FC<{ children: ReactNode; id?: string }> = ({ children, id }) => (
  <h3
    id={id}
    style={{
      margin: '18px 4px 8px',
      fontSize: 13.5,
      fontWeight: 800,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: C.teal,
      scrollMarginTop: 72,
    }}
  >
    {children}
  </h3>
);

/** Lien « Voir tableau dilution » qui fait défiler jusqu'au tableau de la même page. */
const VoirTableauDilution: FC<{ target: string }> = ({ target }) => (
  <button
    type="button"
    onClick={() =>
      document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    style={{
      background: 'none',
      border: 'none',
      padding: 0,
      font: 'inherit',
      color: C.warn,
      fontWeight: 700,
      textDecoration: 'underline',
      cursor: 'pointer',
    }}
  >
    ⚠ Voir les doses de dilution
  </button>
);

/**
 * Doses prêtes à l'emploi, sans calcul pour l'agent : la dose concrète pour
 * chaque contenant courant + un mini-calcul « j'ai N litres d'eau → j'ajoute ».
 * On ne manipule plus de pourcentage sur le terrain.
 */
const DoseCalculator: FC<{
  usages: { label: string; pct: number }[];
  accent?: string;
  id?: string;
  title?: string;
}> = ({ usages, accent = C.teal, id, title }) => {
  const [sel, setSel] = useState(0);
  const [liters, setLiters] = useState(10);
  const pct = usages[sel].pct;

  const fmt = (ml: number): string =>
    ml >= 1000 ? `${(ml / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} L` : `${Math.round(ml)} mL`;
  const dose = (waterL: number): string => fmt(waterL * pct * 10);
  const setL = (v: number): void => setLiters(Math.max(1, Math.min(1000, Math.round(v) || 1)));

  const PRESETS: { l: number; label: string }[] = [
    { l: 5, label: 'Pulvérisateur 5 L' },
    { l: 10, label: 'Seau 10 L' },
    { l: 100, label: 'Cuve 100 L' },
  ];
  const btn: React.CSSProperties = {
    width: 34,
    height: 34,
    borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.2)',
    background: 'rgba(255,255,255,0.06)',
    color: C.white,
    fontSize: 20,
    fontWeight: 700,
    lineHeight: 1,
    cursor: 'pointer',
  };

  return (
    <div
      id={id}
      style={{
        margin: '4px 2px 18px',
        borderRadius: 14,
        overflow: 'hidden',
        border: `2px solid ${accent}`,
        background: 'rgba(255,255,255,0.03)',
        boxShadow: `0 8px 24px ${accent}26`,
        scrollMarginTop: 72,
      }}
    >
      <div
        style={{
          background: accent,
          color: '#0B1628',
          padding: '11px 14px',
          fontSize: 14,
          fontWeight: 900,
          letterSpacing: '0.02em',
        }}
      >
        💧 {title ?? 'Dilution — dose prête à l’emploi'}
      </div>
      <div style={{ padding: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, marginBottom: 12 }}>
        Aucun pourcentage à calculer — lis la dose ou tape ton volume d’eau.
      </div>

      {usages.length > 1 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {usages.map((u, i) => (
            <button
              key={u.label}
              type="button"
              onClick={() => setSel(i)}
              style={{
                padding: '6px 12px',
                borderRadius: 100,
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                border: `1.5px solid ${i === sel ? accent : 'rgba(255,255,255,0.18)'}`,
                background: i === sel ? accent : 'transparent',
                color: i === sel ? '#0B1628' : C.muted,
              }}
            >
              {u.label}
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 12 }}>
        {PRESETS.map((p) => (
          <div
            key={p.l}
            style={{
              padding: '10px 8px',
              borderRadius: 10,
              background: 'rgba(255,255,255,0.05)',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>{p.label}</div>
            <div style={{ fontSize: 20, fontWeight: 900, color: accent, lineHeight: 1 }}>{dose(p.l)}</div>
            <div style={{ fontSize: 10.5, color: C.muted, marginTop: 3 }}>de produit</div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
          padding: '10px 12px',
          borderRadius: 10,
          background: 'rgba(0,0,0,0.18)',
        }}
      >
        <span style={{ fontSize: 13, color: C.white }}>J&rsquo;ai</span>
        <button type="button" style={btn} onClick={() => setL(liters - 1)} aria-label="Moins un litre">
          −
        </button>
        <input
          type="number"
          inputMode="numeric"
          value={liters}
          onChange={(e) => setL(Number(e.target.value))}
          style={{
            width: 56,
            height: 34,
            textAlign: 'center',
            borderRadius: 8,
            border: '1px solid rgba(255,255,255,0.2)',
            background: 'rgba(255,255,255,0.06)',
            color: C.white,
            fontSize: 16,
            fontWeight: 700,
          }}
        />
        <button type="button" style={btn} onClick={() => setL(liters + 1)} aria-label="Plus un litre">
          +
        </button>
        <span style={{ fontSize: 13, color: C.white }}>L d&rsquo;eau → j&rsquo;ajoute</span>
        <b style={{ fontSize: 18, color: accent }}>{dose(liters)}</b>
        <span style={{ fontSize: 13, color: C.white }}>de produit</span>
      </div>
      </div>
    </div>
  );
};

const Card: FC<{ children: ReactNode; style?: React.CSSProperties }> = ({
  children,
  style,
}) => (
  <div
    style={{
      padding: '14px',
      background: 'var(--bg-card)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 12,
      color: C.white,
      fontSize: 16,
      lineHeight: 1.55,
      ...style,
    }}
  >
    {children}
  </div>
);

type AlertKind = 'info' | 'warn' | 'danger' | 'blue';
const Alert: FC<{ kind: AlertKind; children: ReactNode }> = ({ kind, children }) => {
  const cfg = {
    info: { bg: 'rgba(49,220,200,0.08)', border: 'rgba(49,220,200,0.4)', leftBar: C.teal },
    warn: { bg: C.warnBg, border: C.warnBorder, leftBar: C.warn },
    danger: { bg: C.dangerBg, border: C.dangerBorder, leftBar: C.dangerStrong },
    blue: { bg: C.infoBg, border: C.infoBorder, leftBar: C.info },
  }[kind];
  return (
    <div
      style={{
        margin: '10px 0',
        padding: '11px 13px',
        background: cfg.bg,
        border: `1px solid ${cfg.border}`,
        borderLeft: `4px solid ${cfg.leftBar}`,
        borderRadius: 10,
        color: 'var(--text-primary)',
        fontSize: 15.5,
        lineHeight: 1.55,
      }}
    >
      {children}
    </div>
  );
};

const Bullets: FC<{ items: ReactNode[]; tone?: 'default' | 'teal' | 'danger' }> = ({
  items,
  tone = 'default',
}) => {
  const dotColor =
    tone === 'teal' ? C.teal : tone === 'danger' ? C.dangerStrong : 'var(--text-muted)';
  return (
    <ul style={{ margin: '6px 0 4px', padding: 0, listStyle: 'none' }}>
      {items.map((it, i) => (
        <li
          key={i}
          style={{
            margin: '6px 0',
            display: 'flex',
            gap: 10,
            color: C.white,
            fontSize: 16,
            lineHeight: 1.5,
          }}
        >
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: dotColor,
              flexShrink: 0,
              marginTop: 8,
            }}
          />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
};

const Step: FC<{ n: number; title: string; body: ReactNode; tone?: 'default' | 'teal' }> = ({
  n,
  title,
  body,
  tone = 'default',
}) => (
  <div
    style={{
      display: 'flex',
      gap: 10,
      margin: '10px 0',
      padding: '10px 12px',
      background: 'var(--bg-card)',
      border: '1px solid var(--border-subtle)',
      borderRadius: 10,
    }}
  >
    <div
      style={{
        width: 26,
        height: 26,
        borderRadius: 8,
        background: tone === 'teal' ? C.teal : 'rgba(49,220,200,0.16)',
        color: tone === 'teal' ? C.navy : C.teal,
        fontSize: 13.5,
        fontWeight: 900,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {n}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 3 }}>
        {title}
      </div>
      <div style={{ fontSize: 15, color: C.white, lineHeight: 1.5 }}>{body}</div>
    </div>
  </div>
);

const InfoRow: FC<{ k: ReactNode; v: ReactNode }> = ({ k, v }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      padding: '7px 0',
      borderBottom: '1px solid var(--border-subtle)',
      gap: 14,
    }}
  >
    <div style={{ fontSize: 14.5, color: C.muted, fontWeight: 600 }}>{k}</div>
    <div style={{ fontSize: 16, color: 'var(--text-primary)', fontWeight: 700, textAlign: 'right' }}>
      {v}
    </div>
  </div>
);

const SectionWrap: FC<{ children: ReactNode }> = ({ children }) => (
  <div style={{ padding: '12px 14px 28px', maxWidth: 800, margin: '0 auto' }}>
    {children}
  </div>
);

const Hero: FC<{
  eyebrow: string;
  title: string;
  desc?: string;
  accent: string;
}> = ({ eyebrow, title, desc, accent }) => (
  <div
    style={{
      padding: '14px 16px',
      margin: '0 -14px 8px',
      background: `linear-gradient(135deg, ${accent}28, ${accent}05)`,
      borderTop: `3px solid ${accent}`,
      borderBottom: '1px solid var(--border-subtle)',
    }}
  >
    <div
      style={{
        fontSize: 9.5,
        fontWeight: 800,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: accent,
        marginBottom: 4,
      }}
    >
      {eyebrow}
    </div>
    <h1
      style={{
        margin: 0,
        fontSize: 21,
        fontWeight: 900,
        color: 'var(--text-primary)',
        letterSpacing: '-0.015em',
        lineHeight: 1.25,
      }}
    >
      {title}
    </h1>
    {desc && (
      <div
        style={{
          fontSize: 14.5,
          color: 'var(--text-secondary)',
          marginTop: 6,
          lineHeight: 1.5,
        }}
      >
        {desc}
      </div>
    )}
  </div>
);

/* ============================================================ */
/* Section : ACCUEIL                                             */
/* ============================================================ */

const Accueil: FC = () => (
  <SectionWrap>
    <Hero
      eyebrow="📘 Bienvenue chez"
      title="Camion Net — Votre guide terrain"
      desc="Conçu pour aider les salariés à s'approprier l'histoire, la culture et les procédures de l'entreprise."
      accent={C.teal}
    />

    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: 8,
        margin: '12px 0',
      }}
    >
      {[
        { v: '25+', l: 'Unités mobiles HP' },
        { v: '50+', l: 'Collaborateurs' },
        { v: '130K', l: 'Véhicules/an' },
      ].map((s, i) => (
        <div
          key={i}
          style={{
            padding: '12px 8px',
            textAlign: 'center',
            background: 'rgba(49,220,200,0.06)',
            border: '1px solid rgba(49,220,200,0.2)',
            borderRadius: 10,
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 900, color: C.teal, lineHeight: 1 }}>
            {s.v}
          </div>
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              color: C.muted,
              marginTop: 4,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {s.l}
          </div>
        </div>
      ))}
    </div>

    <SecT>Mot du dirigeant & présentation</SecT>
    <Card>
      <p style={{ margin: '0 0 10px' }}>
        Fondée en <b>2002 par Sébastien Zabner</b>, professionnel du transport et de la
        logistique, Camion Net est une entreprise française spécialisée dans le{' '}
        <b>lavage industriel de véhicules</b>. Depuis plus de vingt ans, la société
        accompagne les acteurs du transport, de la logistique, de l&rsquo;agroalimentaire
        et de l&rsquo;industrie en leur proposant des solutions de nettoyage performantes,
        adaptées à leurs contraintes opérationnelles et réglementaires.
      </p>
      <p style={{ margin: '0 0 10px' }}>
        L&rsquo;activité historique repose sur le <b>lavage mobile de camions</b> : nos
        équipes interviennent directement sur les sites de nos clients, pendant les temps
        d&rsquo;immobilisation des véhicules, afin d&rsquo;optimiser leur disponibilité et
        de réduire les coûts liés aux déplacements vers des stations de lavage externes.
      </p>
      <p style={{ margin: '0 0 10px' }}>
        En complément, Camion Net dispose d&rsquo;un <b>centre fixe de lavage à Rungis</b>,
        au cœur du Marché d&rsquo;Intérêt National. Ce site stratégique répond aux besoins
        spécifiques des grands pôles logistiques et des professionnels soumis à des
        exigences élevées en matière d&rsquo;hygiène, notamment dans le secteur
        alimentaire.
      </p>
      <p style={{ margin: 0 }}>
        Avec une flotte de <b>25 unités mobiles haute pression</b>, plus de{' '}
        <b>50 collaborateurs</b> et une présence dans <b>75 départements</b>, Camion Net
        assure une couverture nationale homogène. Chaque année, plus de{' '}
        <b>130 000 véhicules</b> sont entretenus par nos soins.
      </p>
    </Card>

    <SecT>Pourquoi ce livret ?</SecT>
    <Card>
      <p style={{ margin: '0 0 8px' }}>
        Ce document doit évoluer au fil du temps pour accompagner le salarié tout au long
        de sa vie professionnelle. Il devient alors une référence utile, un guide fiable
        et accessible.
      </p>
      <Bullets
        items={[
          'Procédures de lavage et fiches produits à portée de main',
          'Numéros d\'urgence indispensables',
          'Bonnes pratiques terrain et outils numériques obligatoires',
          'Règlement intérieur complet',
          'Suivi et entretien du véhicule et du matériel',
        ]}
        tone="teal"
      />
    </Card>

    <Card style={{ marginTop: 12, textAlign: 'center', fontSize: 12.5, color: C.muted }}>
      <b style={{ color: 'var(--text-primary)' }}>CAMION NET — SAS LZ NET</b>
      <br />
      8, rue du chêne fourchet — 72440 BOULOIRE
      <br />
      Tél / Fax : 02 43 63 31 50 · Portable : 06 64 48 47 64
      <br />
      contact@camionnet.fr · www.camionnet.fr
    </Card>
  </SectionWrap>
);

/* ============================================================ */
/* Section : CONTACTS                                            */
/* ============================================================ */

const Contacts: FC = () => (
  <SectionWrap>
    <Hero
      eyebrow="🚨 Urgences"
      title="Contacts urgences"
      desc="En cas d'urgence sur le terrain, appelez immédiatement le numéro correspondant."
      accent={C.danger}
    />

    <Card>
      <InfoRow k="🚑 SAMU" v={<b style={{ color: C.dangerStrong, fontSize: 16 }}>15</b>} />
      <InfoRow
        k="🚒 Pompiers"
        v={<b style={{ color: C.dangerStrong, fontSize: 16 }}>18</b>}
      />
      <InfoRow
        k="🚔 Police / Gendarmerie"
        v={<b style={{ color: C.dangerStrong, fontSize: 16 }}>17</b>}
      />
      <InfoRow
        k="☎️ Urgences Europe"
        v={<b style={{ color: C.dangerStrong, fontSize: 16 }}>112</b>}
      />
      <InfoRow
        k="☠️ Centre antipoison (INRS/ORFILA)"
        v={<span style={{ color: C.danger, fontSize: 14 }}>01 45 42 59 59</span>}
      />
    </Card>
  </SectionWrap>
);

/* ============================================================ */
/* Section : PRATIQUES                                           */
/* ============================================================ */

const Pratiques: FC = () => (
  <SectionWrap>
    <Hero
      eyebrow="✅ Bonnes pratiques"
      title="Pratiques terrain"
      desc="Ces règles s'appliquent à toutes les interventions. Leur respect conditionne la qualité du service."
      accent={C.ok}
    />

    <SecT>1 · Outils et applications</SecT>
    <Card style={{ marginBottom: 8 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 10,
            background: '#0B1A2F',
            color: C.teal,
            fontSize: 10,
            fontWeight: 900,
            letterSpacing: '0.04em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            lineHeight: 1.1,
            flexShrink: 0,
            border: `1px solid ${C.teal}55`,
          }}
        >
          CAM
          <br />
          NET
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-primary)', marginBottom: 3 }}>
            app.camionnet / agent
          </div>
          <div style={{ fontSize: 13, color: C.white, lineHeight: 1.45 }}>
            Consultation du planning + saisie quotidienne de l&rsquo;activité.
            <b> Utilisation quotidienne obligatoire.</b>
          </div>
        </div>
      </div>
    </Card>
    <Card>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: 10,
            background: C.teal,
            color: 'var(--text-primary)',
            fontSize: 10,
            fontWeight: 900,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            lineHeight: 1.1,
            flexShrink: 0,
          }}
        >
          SKL
          <br />
          LO
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-primary)', marginBottom: 3 }}>
            Skello — RH
          </div>
          <div style={{ fontSize: 13, color: C.white, lineHeight: 1.45 }}>
            Planning, badgeuse géolocalisée, CP, documents (contrat, fiches de paie).
            <b> Support unique RH.</b>
          </div>
        </div>
      </div>
    </Card>

    <SecT>2 · Badgeage obligatoire sur chantier</SecT>
    <Alert kind="info">
      <b>Chaque intervenant doit badger à l&rsquo;arrivée et au départ.</b> Ce badgeage
      permet à nos clients de connaître nos présences sur site pour des raisons de
      sécurité.
    </Alert>
    <Card>
      <Bullets
        items={[
          <>
            Badger <b>à l&rsquo;arrivée</b> dès l&rsquo;accès au site client
          </>,
          <>
            Badger <b>au départ</b> une fois l&rsquo;intervention terminée
          </>,
          'Obligation de traçabilité : sécurité des intervenants et conformité client',
          'Un oubli de badgeage doit être signalé immédiatement au Responsable d’Exploitation',
        ]}
        tone="teal"
      />
    </Card>

    <SecT>3 · Congés et absences</SecT>
    <Card>
      <Bullets
        items={[
          <>
            Demande via <b>Skello</b>, au minimum <b>1 mois avant</b>
          </>,
          'Une demande ne vaut pas validation — délai de validation : 1 semaine',
          'Chaque demande fait l’objet d’une validation Skello + communication RH',
          <>
            Toute absence non autorisée peut constituer une <b>faute disciplinaire</b>
          </>,
        ]}
      />
    </Card>

    <SecT>4 · Communication opérationnelle</SecT>
    <Card>
      <Bullets
        items={[
          <>
            <b>Modification de planning</b> → communiquer immédiatement au Responsable
            d&rsquo;Exploitation, informer le client, trouver une solution alternative
          </>,
          <>
            <b>Incident chez un client</b> → remonter immédiatement au Responsable
            d&rsquo;Exploitation, traiter selon les consignes fixées
          </>,
        ]}
        tone="danger"
      />
    </Card>

    <SecT>5 · Responsabilité individuelle</SecT>
    <Card>
      <p style={{ margin: '0 0 10px' }}>
        Chaque salarié est responsable de son matériel. Chaque véhicule doit disposer :
      </p>
      <Bullets
        items={[
          <>
            <b>Matériel en double :</b> nettoyeur HP, flexibles, pistolet, lance, joints,
            brosse, manche, raclette
          </>,
          <>
            <b>Stock suffisant :</b> PL99-AL, DEFI-V.SID et autres produits nécessaires
          </>,
          <>
            <b>EPI complets :</b> chaussures/bottes sécurité, masque COV, gilet HV, gants
            nitrile, lunettes, tenue imperméable
          </>,
          <>
            <b>Pack documents :</b> liste outils/EPI, Livret de Bord
          </>,
        ]}
        tone="teal"
      />
    </Card>

    <SecT>6 · Intervention chez le client (pas à pas)</SecT>
    <Step n={1} title="Arriver avec les EPI obligatoires" body="Gilet haute visibilité + chaussures de sécurité dès le franchissement du portail." />
    <Step n={2} title="Se présenter au référent" body="Prendre les consignes auprès de l'exploitation : sécurité, circulation, consignes d'intervention." />
    <Step n={3} title="Préparer la zone de lavage" body="Selon les recommandations du client. Signaler immédiatement tout problème (sécurité, accès, matériel, véhicule)." />
    <Step n={4} title="Réaliser la prestation" body="Respecter strictement les consignes de sécurité pendant toute l'intervention." />
    <Step n={5} tone="teal" title="Fin de prestation : faire signer le bon" body="Faire signer et tamponner le bon par un représentant du client. Conserver un exemplaire pour l'entreprise. Avertir le référent du départ." />
    <Step n={6} tone="teal" title="Badger départ + enregistrer dans l'app" body="Badger sur Skello + enregistrer la prestation dans l'app Camion Net AVEC photo, avant de quitter le site." />

    <Alert kind="warn">
      Les clients ont un accès direct à leurs prestations via l&rsquo;intranet. La qualité
      des informations saisies est <b>essentielle</b> pour la transparence et la relation
      commerciale.
    </Alert>
  </SectionWrap>
);

/* ============================================================ */
/* Section : PRODUITS                                            */
/* ============================================================ */

const ProdHero: FC<{ color: string; badge: string; name: string; tag: string; specs: { v: string; l: string }[] }> = ({
  color,
  badge,
  name,
  tag,
  specs,
}) => (
  <div
    style={{
      padding: '14px',
      background: `linear-gradient(135deg, ${color}22, ${color}06)`,
      border: `1px solid ${color}55`,
      borderRadius: 14,
      marginTop: 8,
    }}
  >
    <div
      style={{
        display: 'inline-block',
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: '0.04em',
        color: color,
        background: `${color}22`,
        padding: '3px 10px',
        borderRadius: 100,
        marginBottom: 8,
      }}
    >
      {badge}
    </div>
    <h2 style={{ margin: '0 0 6px', color: 'var(--text-primary)', fontSize: 19, fontWeight: 900, letterSpacing: '-0.01em' }}>
      {name}
    </h2>
    <div style={{ fontSize: 13, color: C.white, lineHeight: 1.5, marginBottom: 12 }}>{tag}</div>
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${specs.length}, 1fr)`, gap: 6 }}>
      {specs.map((s, i) => (
        <div
          key={i}
          style={{
            padding: '8px 6px',
            background: 'var(--bg-card)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 8,
            textAlign: 'center',
          }}
        >
          <div style={{ color: color, fontSize: 15, fontWeight: 900, lineHeight: 1.1 }}>
            {s.v}
          </div>
          <div style={{ fontSize: 9.5, color: C.muted, marginTop: 3, fontWeight: 600 }}>
            {s.l}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const Table: FC<{ headers: string[]; rows: ReactNode[][] }> = ({ headers, rows }) => (
  <div style={{ overflowX: 'auto', margin: '8px -2px' }}>
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th
              key={i}
              style={{
                background: 'rgba(49,220,200,0.12)',
                color: C.teal,
                padding: '8px 10px',
                textAlign: 'left',
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                borderBottom: `1px solid ${C.teal}55`,
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => (
              <td
                key={j}
                style={{
                  padding: '8px 10px',
                  borderBottom: '1px solid var(--border-subtle)',
                  color: C.white,
                }}
              >
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const EpiGrid: FC<{ items: { icon: string; label: string }[] }> = ({ items }) => (
  <div
    style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
      gap: 6,
      margin: '8px 0',
    }}
  >
    {items.map((it, i) => (
      <div
        key={i}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 10px',
          background: 'rgba(49,220,200,0.06)',
          border: '1px solid rgba(49,220,200,0.25)',
          borderRadius: 10,
        }}
      >
        <span style={{ fontSize: 20, flexShrink: 0 }}>{it.icon}</span>
        <span style={{ fontSize: 12.5, color: C.white, lineHeight: 1.3 }}>{it.label}</span>
      </div>
    ))}
  </div>
);

const ProduitsPL99: FC = () => (
  <SectionWrap>
    <Hero
      eyebrow="🟧 PL99-AL"
      title="Nettoyage extérieur et intérieur caisse «classique»"
      desc="Shampooing alcalin polyvalent ABAX — pour véhicules, autobus, bâches, matériel agricole, caisses frigo (intérieur)."
      accent={C.warn}
    />

    <ProdHero
      color={C.warn}
      badge="🟧 Produit 1 — ABAX · Réf. 0108-2801"
      name="SHAMPOOING HP PL99-AL"
      tag="Lavage — Nettoyage extérieurs véhicules & intérieurs caisses classiques. Produit alcalin polyvalent économique, soluble en eau froide, qualité alimentaire."
      specs={[
        { v: 'pH 13', l: 'pH pur à 20°C' },
        { v: '1,14', l: 'Densité 20°C' },
        { v: 'Jaune', l: 'Liquide limpide' },
      ]}
    />

    <DoseCalculator
      id="dilution-pl99"
      accent={C.warn}
      title="Dilution PL99 — dose prête à l’emploi"
      usages={[
        { label: 'Régulier (courant)', pct: 5 },
        { label: 'Intensif (incrusté)', pct: 10 },
      ]}
    />

    <SecT>Domaines d&rsquo;application</SecT>
    <Card>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {['Poids lourds', 'Autobus', 'Bâches de camion', 'Matériel agricole', 'Carrosseries autos', 'Caisses frigo (intérieur)'].map((t, i) => (
          <span
            key={i}
            style={{
              fontSize: 12.5,
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: 100,
              background: `${C.warn}22`,
              color: C.warn,
              border: `1px solid ${C.warn}55`,
            }}
          >
            {t}
          </span>
        ))}
      </div>
      <Alert kind="warn">
        <b>⚠️ Déconseillé</b> sur 2-roues et parties revêtues (chromes, aciers
        galvanisés, aluminiums). Essai préalable obligatoire. Alternatives : SHAMPOOING
        HP 2000 ou HP 3000.
      </Alert>
    </Card>

    <SecT>Tableau de dilution — détail technique</SecT>
    <Table
      headers={['Usage', 'Dilution', 'Notes']}
      rows={[
        ['Pulvérisation (le + courant)', <b style={{ color: C.warn }}>5 à 10 %</b>, 'Bas→haut · 3-5 min · rincer HP'],
        ['Intensif (salissures fortes)', <b style={{ color: C.warn }}>10 % (1:10)</b>, '—'],
        ['Régulier', <b style={{ color: C.warn }}>5 % (1:20)</b>, '—'],
        ['Manuel (seau)', <b style={{ color: C.warn }}>2 à 4 %</b>, 'Gant · rincer'],
        ['HP froid (bac intégré)', <b style={{ color: C.warn }}>2 à 3 %</b>, 'Aspersion bas→haut BP, rincer haut→bas'],
      ]}
    />
    <Alert kind="info">
      💧 Après utilisation, préconiser l&rsquo;emploi de l&rsquo;<b>AUTOSÉCHANT
      VIT&apos;SEC</b> pour finition optimale.
    </Alert>

    <SecT>EPI obligatoires — PL99-AL</SecT>
    <EpiGrid
      items={[
        { icon: '🧤', label: 'Gants Latex / PVC / Nitrile / Néoprène' },
        { icon: '🥽', label: 'Lunettes ou visière (NF EN166)' },
        { icon: '🦺', label: 'Gilet haute visibilité' },
        { icon: '👢', label: 'Bottes / chaussures étanches' },
        { icon: '🥼', label: 'Tenue couvrante imperméable' },
        { icon: '🙌', label: 'Laver les mains après utilisation' },
      ]}
    />
    <Alert kind="danger">
      <b>🔴 Danger H314</b> — Corrosion cutanée Cat.1 (H314), lésions oculaires graves
      Cat.1 (H318). GHS05. <b>P280</b> : porter gants, vêtements, équipement de
      protection yeux/visage. Enlever et laver les vêtements contaminés. Fontaines
      oculaires recommandées. FDS : fds-msds@abax.eu · Urgence : 01 45 42 59 59
    </Alert>

    <SecT>Procédure — Intérieur caisse frigorifique</SecT>
    <Step n={1} title="Préparation" body="Caisse vide, équipements électriques éteints, éléments sensibles protégés. Système d'évacuation eaux usées conforme." />
    <Step n={2} title="Solution" body={<>Diluer eau tiède/froide : <b>1:10 intensif</b> ou <b>1:20 régulier</b>. Pulvériseur ou mousseur. <VoirTableauDilution target="dilution-pl99" /></>} />
    <Step n={3} title="Application" body="Murs, sol, plafond, portes. Insister coins, joints. Laisser agir 5 à 10 min sans laisser sécher." />
    <Step n={4} title="Nettoyage mécanique" body="Brosse ou éponge. Balai de lavage pour les zones en hauteur." />
    <Step n={5} tone="teal" title="Rinçage HP (haut → bas)" body="Eau claire HP, éliminer tous les résidus de savon (joints, angles)." />
    <Step n={6} tone="teal" title="Séchage" body="Évacuer à la raclette. Portes ouvertes pour séchage. Désinfectant si requis après séchage complet." />
    <Step n={7} tone="teal" title="Contrôle et enregistrement" body="Vérifier propreté. Enregistrer sur l'app Camion Net." />

    <SecT>Procédure — Extérieur caisse</SecT>
    <Step n={1} title="Préparation matériel" body={<>Vérifier évacuation eaux grises. Diluer PL99 : <b>1:10</b> (incrusté) ou <b>1:20</b> (courant). <VoirTableauDilution target="dilution-pl99" /></>} />
    <Step n={2} title="Rinçage préliminaire" body="Eau claire HP. Insister bas de caisse, roues, zones exposées." />
    <Step n={3} title="Application savon (bas → haut)" body="Pulvérisation uniforme. Laisser agir 3-5 min sans laisser sécher." />
    <Step n={4} title="Nettoyage mécanique (haut → bas)" body="Brosse télescopique. Zones difficiles et taches persistantes." />
    <Step n={5} tone="teal" title="Rinçage final HP + contrôle" body="Vérifier absence de traces de savon. Rétroviseurs, joints, plaques signalétiques propres et intacts." />
    <Step n={6} tone="teal" title="Séchage + enregistrement" body="Air libre ou microfibre. Enregistrer sur l'app." />

    <SecT>Conditionnements PL99-AL</SecT>
    <Card>
      <InfoRow k="01085" v="Bidon plastique 5 kg" />
      <InfoRow k="010820" v="Bidon plastique 20 kg" />
      <InfoRow k="0108210" v="Fût 210 kg" />
      <InfoRow k="01081000" v="Container 1 000 kg" />
      <InfoRow
        k="❄️ Stockage"
        v={<span style={{ color: C.teal }}>À l&rsquo;abri du gel</span>}
      />
    </Card>

  </SectionWrap>
);

/* ============================================================ */
/* Section : PRODUITS DEFI-V.SID                                 */
/* ============================================================ */

const ProduitsDefiv: FC = () => (
  <SectionWrap>
    <Hero
      eyebrow="🟦 DEFI-V.SID"
      title="Nettoyant intérieur caisse frigorifique"
      desc="Désinfectant bactéricide, fongicide et virucide — SID France. Biocide TP2 & TP4. Conforme EN 1276 et EN 14476+A2."
      accent={C.info}
    />

    <ProdHero
      color={C.info}
      badge="🟦 Produit 2 — SID France · Code B21 · FDS Réf. PS 2573"
      name="DEFI-V.SID"
      tag="Shampooing intérieur & extérieur pour camions frigorifiques. Hygiène bactéricide. Biocide TP2 et TP4. Inventaire n°37483. Contient Chlorure de Didécyldiméthylammonium 5 g/L."
      specs={[
        { v: 'pH 13,5', l: 'pH pur (13-14)' },
        { v: '1 140', l: 'kg/m³ densité' },
        { v: 'Citron', l: 'Liquide ambré' },
      ]}
    />

    <DoseCalculator
      id="dilution-defiv"
      accent={C.info}
      title="Dilution DEFI-V — dose prête à l’emploi"
      usages={[
        { label: 'Standard (désinfection)', pct: 2 },
        { label: 'Salissures tenaces', pct: 5 },
      ]}
    />

    <SecT>Certifications & normes</SecT>
    <Card>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {['EN 1276 — Bactéricide', 'EN 14476+A2 — Virucide', 'COVID-19', 'TP2 — Surfaces', 'TP4 — Denrées alim.', 'ISO 9001', 'ISO 14001', 'Arrêté 08/09/1999'].map((t, i) => (
          <span
            key={i}
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: '4px 10px',
              borderRadius: 100,
              background: `${C.info}22`,
              color: '#7AB3F5',
              border: `1px solid ${C.info}55`,
            }}
          >
            {t}
          </span>
        ))}
      </div>
      <div style={{ marginTop: 10, fontSize: 13, color: C.white, lineHeight: 1.5 }}>
        <b>Activité microbiologique :</b> bactéricide selon EN 1276 à 2 %, 5 min, 20°C en
        conditions de saleté sur <i>Staphylococcus aureus, Pseudomonas aeruginosa, E.
        coli, Enterococcus hirae</i>. Virucidie partielle selon EN 14476+A2 à 2 %, 15 min,
        20°C sur virus de la vaccine.
      </div>
    </Card>

    <SecT>Tableau de dilution — détail technique</SecT>
    <Table
      headers={['Usage', 'Dilution', 'Temps de contact']}
      rows={[
        ['Tout-en-1 (tracteur + remorque)', <b style={{ color: C.info }}>2 %</b>, '5 min puis rinçage HP'],
        [<b>Action bactéricide (EN 1276)</b>, <b style={{ color: C.info }}>≥ 2 %</b>, <b>5 min min.</b>],
        [<b>Action virucide (EN 14476)</b>, <b style={{ color: C.info }}>≥ 2 %</b>, <b>15 min min.</b>],
        ['Shampooing (salissures tenaces)', <b style={{ color: C.info }}>5 %</b>, 'Rinçage HP'],
        ['Portique automatique', <b style={{ color: C.info }}>0,8 à 1,5 %</b>, 'Régler pompe'],
        ['Sol & parois', <b style={{ color: C.info }}>0,8 à 2 %</b>, 'Action + HP'],
      ]}
    />
    <Alert kind="info">
      💡 Si les conditions bactéricide (2 %, 5 min) et virucide (2 %, 15 min) sont
      respectées, <b>l&rsquo;action biocide peut être réalisée en même temps que le
      nettoyage — 1 seule opération.</b>
    </Alert>

    <SecT>EPI obligatoires — DEFI-V.SID</SecT>
    <EpiGrid
      items={[
        { icon: '🧤', label: 'Gants PVC / Nitrile / Latex (EN 374)' },
        { icon: '🥽', label: 'Lunettes anti-éclaboussures (EN 166)' },
        { icon: '🦺', label: 'Gilet haute visibilité' },
        { icon: '👢', label: 'Bottes / chaussures étanches (EN 344)' },
        { icon: '🥼', label: 'Vêtement protection corps' },
        { icon: '😷', label: 'Masque FFP1 si ventilation insuff. (EN 141)' },
      ]}
    />
    <Alert kind="danger">
      <b>🔴 H314</b> — Brûlures peau et lésions oculaires graves. <b>H412</b> — Nocif
      organismes aquatiques. GHS05.
      <br />
      <br />
      <b>Contact peau :</b> retirer vêtements, rincer 15 min, médecin.
      <br />
      <b>Contact yeux :</b> rincer 10-15 min, retirer lentilles, ophtalmologiste.
      <br />
      <b>Ingestion :</b> NE PAS faire vomir. Antipoison : <b>01 45 42 59 59</b> ou{' '}
      <b>15</b>.
      <br />
      Ne pas respirer fumées (P260). Éviter rejet environnement (P273).
    </Alert>

    <SecT>Procédure complète — Lavage & désinfection caisse frigorifique</SecT>
    <Step n={1} tone="teal" title="Préparation" body={<>Aire avec arrivée d&rsquo;eau et récupération eaux usées (séparateur hydrocarbures). EPI. Préparer solution <b>2 %</b> (2 L / 100 L). <VoirTableauDilution target="dilution-defiv" /></>} />
    <Step n={2} tone="teal" title="Pré-rinçage HP" body="Caisse vide, exempte de déchets. Eau claire HP pour enlever poussières et déchets grossiers." />
    <Step n={3} tone="teal" title="Application (bas → haut)" body="Solution 2 % sur parois, sol, plafond. Insister zones souillées : graisses, sang, restes." />
    <Step n={4} tone="teal" title="Action mécanique" body="Brosser manuellement ou lance télescopique." />
    <Step n={5} title="⏱ Temps de contact" body={<><b>5 min</b> action bactéricide (EN 1276) · <b>15 min</b> action virucide (EN 14476). Action biocide possible en parallèle du nettoyage.</>} />
    <Step n={6} tone="teal" title="Rinçage HP (haut → bas) à l'eau potable" body="Éliminer résidus joints et angles. Raclette. Portes ouvertes pour séchage." />
    <Step n={7} tone="teal" title="Contrôle et enregistrement" body="Toutes zones traitées, absence de traces. Enregistrer sur l'app." />

    <Alert kind="warn">
      <b>⚠ Points de vigilance DEFI-V :</b> Ne pas dépasser 10 % de dilution. Toujours
      rincer après usage (pH ~12,5). Contact avec acier, métaux galvanisés, aluminium →
      corrosion + hydrogène inflammable. Ne pas mélanger avec d&rsquo;autres produits. Ne
      pas conserver dans aluminium/galvanisé. Incompatible acides forts.
    </Alert>

    <SecT>Conditionnements DEFI-V.SID</SecT>
    <Card>
      <InfoRow k="Conditionnements" v="Fût 210 L · Bidon 20 L · 4×5 L · Vrac" />
      <InfoRow k="Température stockage" v="0 à 40 °C" />
      <InfoRow k="Durée max." v="24 mois" />
      <InfoRow k="❄️ Stockage" v={<span style={{ color: C.teal }}>Emballage origine, abri gel</span>} />
      <InfoRow k="Incompatibilités" v="Acides forts, oxydants forts" />
      <InfoRow k="Transport" v="Corrosif · UN 1824 · Classe 8" />
    </Card>
  </SectionWrap>
);

/* ============================================================ */
/* Section : REGLEMENT                                           */
/* ============================================================ */

const Reglement: FC = () => (
  <SectionWrap>
    <Hero
      eyebrow="📜 Règlement intérieur"
      title="Règlement intérieur"
      desc="S'applique à tous les salariés, stagiaires et intérimaires sur tous les sites. Fait à Bouloire le 16 janvier 2026."
      accent={C.info}
    />

    <SecT>1 · Objet et champ d&rsquo;application</SecT>
    <Card>
      Ce règlement définit les règles d&rsquo;hygiène, sécurité, discipline et bon
      fonctionnement de CAMION NET. Il s&rsquo;applique au centre de lavage, bureaux,
      parkings, véhicules de l&rsquo;entreprise et sites clients.
    </Card>

    <SecT>2 · Principes fondamentaux</SecT>
    {[
      { icon: '🤝', title: 'Respect', body: 'Comportement professionnel, courtois et respectueux envers collègues, direction, clients et partenaires.' },
      { icon: '🛡️', title: 'Sécurité', body: 'La sécurité prime sur toute autre considération. Consignes, protocoles et EPI scrupuleusement respectés.' },
      { icon: '🔒', title: 'Confidentialité', body: 'Informations sur l\'entreprise, clients et salariés non divulguées en dehors du cadre professionnel.' },
      { icon: '⚖️', title: 'Égalité de traitement', body: 'Zéro tolérance pour propos ou comportements discriminatoires, racistes, sexistes, humiliants ou violents.' },
    ].map((p, i) => (
      <Card key={i} style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 14.5, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>
          {p.icon} {p.title}
        </div>
        <div style={{ fontSize: 13.5, color: C.white, lineHeight: 1.5 }}>{p.body}</div>
      </Card>
    ))}

    <SecT>3 · Organisation du temps de travail</SecT>
    <Card style={{ marginBottom: 8 }}>
      <b style={{ color: 'var(--text-primary)' }}>3.1 Horaires et présence</b>
      <p style={{ margin: '4px 0 0' }}>
        Horaires précisés dans contrat et planning. Pauses intégrées selon les
        dispositions légales. Se présenter en tenue, prêt à exercer au début du service.
      </p>
    </Card>
    <Card style={{ marginBottom: 8 }}>
      <b style={{ color: 'var(--text-primary)' }}>3.2 Retards et absences</b>
      <p style={{ margin: '4px 0 0' }}>
        Tout retard signalé immédiatement à la direction et au client si concerné.
        Retards répétés ou injustifiés → sanctions disciplinaires. Toute absence justifiée
        et portée à la connaissance de l&rsquo;entreprise sans délai.
      </p>
    </Card>
    <Card>
      <b style={{ color: 'var(--text-primary)' }}>3.3 Congés payés</b>
      <p style={{ margin: '4px 0 0' }}>
        Demande au minimum <b>30 jours à l&rsquo;avance</b> via Skello. Validation
        expresse requise. Absence non autorisée = faute disciplinaire possible.
      </p>
    </Card>

    <SecT>4 · Hygiène, sécurité et conditions de travail</SecT>
    <Alert kind="warn">
      🛡️ La sécurité des personnes et des biens est une <b>priorité absolue</b>. Chaque
      salarié veille à sa sécurité et celle des autres, respecte les consignes et signale
      immédiatement toute situation dangereuse.
    </Alert>
    <Card style={{ marginBottom: 8 }}>
      <b style={{ color: 'var(--text-primary)' }}>4.1 EPI</b>
      <p style={{ margin: '4px 0 4px' }}>
        EPI fournis : tenue, chaussures/bottes sécurité, gilet HV, gants, lunettes, masque
        respiratoire, combinaison, cônes de balisage.
      </p>
      <p style={{ margin: 0 }}>
        <b>Port obligatoire</b> dès que la situation l&rsquo;exige. Refus répété ={' '}
        <b>faute grave</b>.
      </p>
    </Card>
    <Card style={{ marginBottom: 8 }}>
      <b style={{ color: 'var(--text-primary)' }}>4.2 Produits, machines et lots de bord</b>
      <p style={{ margin: '4px 0 4px' }}>
        Préparer le lot la veille : clés, nettoyeur HP, aspirateur, tuyaux, produits,
        carburant, EPI. Respecter consignes, protocoles et dilutions.
      </p>
      <p style={{ margin: 0 }}>
        Toute panne signalée immédiatement. Usage abusif → sanction disciplinaire.
      </p>
    </Card>
    <Card style={{ marginBottom: 8 }}>
      <b style={{ color: 'var(--text-primary)' }}>4.3 Sécurité routière et téléphone</b>
      <p style={{ margin: '4px 0 0' }}>
        Code de la route impératif. Conduite C/EC réservée aux titulaires du permis.{' '}
        <b>Téléphone au volant interdit</b> (sauf kit mains libres conforme).
      </p>
    </Card>
    <Card>
      <b style={{ color: 'var(--text-primary)' }}>4.4 Tabac, alcool, drogues, médicaments</b>
      <p style={{ margin: '4px 0 0' }}>
        Interdiction de fumer en locaux fermés et véhicules.{' '}
        <b>Alcool/drogues interdits</b> avant et pendant le service. Médicaments
        affectant la vigilance : prendre les dispositions adaptées.
      </p>
    </Card>

    <SecT>5 · Ressources logistiques et informatiques</SecT>
    <Card>
      Matériels informatiques, téléphones, véhicules et logiciels mis à disposition pour
      un <b>usage strictement professionnel</b>. Interdit de diffuser des contenus
      illicites ou des informations confidentielles.
    </Card>

    <SecT>6 · Discipline et sanctions</SecT>
    <Card>
      <p style={{ margin: '0 0 8px' }}>
        Tout manquement peut donner lieu à une sanction disciplinaire :
      </p>
      <Bullets
        items={[
          'Rappel à l’ordre — entretien individuel',
          'Avertissement écrit',
          <b style={{ color: C.danger }}>Mise à pied disciplinaire</b>,
          <b style={{ color: C.danger }}>Toute sanction prévue par la loi — jusqu’au licenciement (faute grave/lourde)</b>,
        ]}
      />
      <Alert kind="info">
        ℹ️ Les <b>sanctions pécuniaires sont interdites</b>. Le non-respect récurrent
        peut impacter les primes liées à assiduité, qualité du travail ou respect des
        procédures.
      </Alert>
    </Card>

    <SecT>7 · Harcèlement et discrimination</SecT>
    <Alert kind="danger">
      🚫 <b>Zéro tolérance.</b> Aucun salarié ne doit subir d&rsquo;agissements de
      harcèlement moral, propos sexuels non désirés, ou discrimination. Toute personne
      victime ou témoin doit signaler à la direction. Enquête interne possible +
      sanctions. Aucune sanction contre une personne ayant dénoncé.
    </Alert>

    <SecT>8 · Vie collective</SecT>
    <Card>
      <Bullets
        items={[
          'Maintenir espaces communs propres et rangés',
          'Limiter le bruit, ne pas perturber les autres',
          'Boissons et aliments dans les espaces prévus',
        ]}
        tone="teal"
      />
    </Card>

    <SecT>9 · Image de l&rsquo;entreprise</SecT>
    <Card>
      <Bullets
        items={[
          'Se présenter en temps et heure',
          'Tenue propre et adaptée (EPI obligatoires)',
          'Respecter les protocoles clients',
          'Politesse, amabilité, professionnalisme',
          <b style={{ color: C.danger }}>S’abstenir de tout propos diffamatoire</b>,
          <b style={{ color: C.danger }}>Aucun comportement raciste, communautariste, humiliant</b>,
        ]}
      />
    </Card>

    <SecT>10 · Applications et primes</SecT>
    <Card>
      <p style={{ margin: '0 0 8px' }}>
        Skello = outil RH. LZ NET CAMION NET = outil prestations, demandes matériel,
        entretien véhicules. Les saisies doivent être <b>exactes, complètes, à jour</b>.
      </p>
      <p style={{ margin: 0 }}>
        Les primes peuvent être liées à respect des consignes, utilisation correcte des
        apps, assiduité, qualité. Non-respect après rappel = sanctions possibles.
      </p>
    </Card>

    <SecT>11 · Dispositions diverses</SecT>
    <Card>
      <p style={{ margin: '0 0 8px' }}>
        En cas de difficulté (santé, addiction, situation personnelle), se rapprocher de
        la direction.
      </p>
      <Bullets
        items={[
          'Drogues Info Service',
          'Alcool Info Service',
          'Permis à points — Portail officiel de l\'administration',
        ]}
      />
    </Card>

    <SecT>12 · Convention collective & entrée en vigueur</SecT>
    <Alert kind="info">
      📄 <b>Convention collective des entreprises de propreté et services associés</b> du
      26 juillet 2011. Consultable sur legifrance.gouv.fr (KALICONT000027172335) et
      tenue à disposition au bureau.
    </Alert>
  </SectionWrap>
);

/* ============================================================ */
/* Section : MATERIEL                                            */
/* ============================================================ */

const Materiel: FC = () => (
  <SectionWrap>
    <Hero
      eyebrow="🛠 Matériel"
      title="Matériel & maintenance"
      desc="Mise en route, pannes fréquentes, stockage et check sécurité des nettoyeurs."
      accent={C.info}
    />

    <SecT>Matériel obligatoire par véhicule (en double)</SecT>
    <Card>
      <Bullets
        items={[
          'Nettoyeur Haute Pression thermique — état de fonctionnement',
          'Flexibles HP (quantité suffisante)',
          'Pistolet et lance — contrôlés avant départ',
          'Joints de rechange',
          'Brosse télescopique + manche + raclette',
          <b style={{ color: C.teal }}>Stock PL99-AL et DEFI-V.SID suffisant</b>,
          <b style={{ color: C.teal }}>EPI complets (voir Pratiques)</b>,
        ]}
      />
    </Card>

    <SecT>Mise en route du nettoyeur HP thermique</SecT>
    <Step n={1} title="Vérifications avant démarrage" body="Niveau carburant, huile moteur, état flexibles/raccords, eau disponible et connectée." />
    <Step n={2} title="Démarrage" body="Ouvrir l'arrivée d'eau. Mettre en marche selon procédure constructeur. Laisser monter en pression avant utilisation." />
    <Step n={3} title="Arrêt et rangement" body="Couper alimentation carburant. Laisser refroidir avant rangement. Purger les flexibles. Protéger du gel en hiver." />

    <SecT>Pannes fréquentes et actions</SecT>
    <Table
      headers={['Problème', 'Cause probable', 'Action']}
      rows={[
        ['Pression insuffisante', 'Flexible plié, filtre bouché', 'Vérifier flexible et filtre. Signaler si pompe défaillante'],
        ['Fuite aux raccords', 'Joint usé ou mal serré', 'Remplacer le joint (stock de bord)'],
        ['Machine ne démarre pas', 'Carburant vide, bougie HS', 'Vérifier niveaux. Signaler si persisté'],
        ['Surchauffe', 'Manque d\'eau, filtre colmaté', 'Arrêter immédiatement. Vérifier circuit eau'],
        ['Vibrations anormales', 'Corps étranger dans pompe', 'Arrêter et signaler au responsable'],
      ]}
    />

    <SecT>Check sécurité avant intervention</SecT>
    <Alert kind="warn">
      <b>Ne jamais démarrer une intervention</b> si défaut visible (flexible fissuré,
      raccord défaillant, fuite carburant). Signaler et attendre remplacement.
    </Alert>
    <Card>
      <Bullets
        items={[
          'Flexibles et raccords : pas de fissure, pas de fuite',
          'Pistolet et lance : verrouillage fonctionnel',
          'Zone de travail : espace dégagé, pas de risque de chute',
          'EPI portés avant toute mise en pression',
          'Évacuation des eaux usées opérationnelle',
        ]}
        tone="teal"
      />
    </Card>
  </SectionWrap>
);

/* ============================================================ */
/* Section : CONVENTION                                          */
/* ============================================================ */

const Convention: FC = () => (
  <SectionWrap>
    <Hero
      eyebrow="📚 Convention"
      title="Convention collective"
      desc="Définit les droits et obligations des salariés et de l'employeur."
      accent={C.info}
    />

    <a
      href="https://www.legifrance.gouv.fr/conv_coll/id/KALICONT000027172335"
      target="_blank"
      rel="noreferrer"
      style={{
        display: 'block',
        padding: '14px 16px',
        background: 'rgba(49,220,200,0.08)',
        border: `1px solid ${C.teal}55`,
        borderRadius: 12,
        textDecoration: 'none',
        margin: '12px 0',
      }}
    >
      <div style={{ fontWeight: 800, fontSize: 15, color: C.teal, marginBottom: 4 }}>
        🔗 Convention collective des entreprises de propreté
      </div>
      <div style={{ fontSize: 13, color: C.white, lineHeight: 1.45 }}>
        Du 26 juillet 2011 — Consultable sur Légifrance
      </div>
    </a>

    <Card>
      <p style={{ margin: '0 0 8px' }}>
        La convention collective applicable à Camion Net est celle des{' '}
        <b>entreprises de propreté et services associés</b>. Elle régit notamment :
      </p>
      <Bullets
        items={[
          'Les classifications et grilles de rémunération',
          'Les congés payés et absences autorisées',
          'Les conditions de travail et sécurité',
          'La prévoyance et la mutuelle d\'entreprise',
        ]}
        tone="teal"
      />
      <p style={{ margin: '8px 0 0' }}>
        Un exemplaire est tenu à la disposition des salariés au bureau et consultable en
        ligne sur Légifrance.
      </p>
    </Card>
  </SectionWrap>
);

/* ============================================================ */
/* Section : VEHICULE                                            */
/* ============================================================ */

const Vehicule: FC = () => (
  <SectionWrap>
    <Hero
      eyebrow="🚐 Véhicule"
      title="Véhicule & équipement"
      desc="Contrôles quotidiens, consommables, sécurité et anomalies à remonter."
      accent={C.info}
    />

    <SecT>Contrôles quotidiens obligatoires</SecT>
    <Card>
      <Bullets
        items={[
          <><b>Niveaux</b> — Huile moteur, refroidissement, frein, lave-glace</>,
          <><b>Pneumatiques</b> — Pression, état visuel, absence de corps étrangers</>,
          <><b>Éclairage</b> — Avant, arrière, clignotants, stop, feux de recul</>,
          <><b>Carburant</b> — Niveau suffisant pour les interventions prévues</>,
          <><b>Documents bord</b> — Carte grise, assurance, contrôle technique, permis</>,
          <><b>Propreté</b> — Habitacle propre, chargement sécurisé et arrimé</>,
        ]}
      />
    </Card>

    <SecT>Règles d&rsquo;utilisation</SecT>
    <Card>
      <Bullets
        items={[
          'Usage strictement professionnel (sauf autorisation écrite)',
          'Code de la route en toutes circonstances',
          <b style={{ color: C.danger }}>Téléphone au volant interdit — kit mains libres exclusivement</b>,
          <b style={{ color: C.danger }}>Alcool/stupéfiants : tolérance zéro</b>,
          'Vitesse adaptée aux conditions (météo, charge, environnement)',
        ]}
      />
    </Card>

    <SecT>Anomalies à remonter immédiatement</SecT>
    <Alert kind="danger">
      <b>Toute anomalie signalée sans délai</b> au Responsable d&rsquo;Exploitation. Ne
      pas attendre la fin de semaine.
    </Alert>
    <Card>
      <Bullets
        items={[
          'Voyant allumé sur le tableau de bord',
          'Bruit anormal (moteur, freins, suspension)',
          'Fuite de fluide (huile, carburant, frein)',
          'Pneu dégonflé ou endommagé',
          'Accident ou accrochage, même mineur — signaler systématiquement',
        ]}
        tone="danger"
      />
    </Card>

    <SecT>Consommables & entretien</SecT>
    <Card>
      <p style={{ margin: '0 0 8px' }}>
        Toute demande de consommable doit être faite auprès du{' '}
        <b>Responsable d&rsquo;Exploitation</b>. Les véhicules doivent être maintenus
        propres et en bon état.
      </p>
      <p style={{ margin: 0, color: C.muted, fontSize: 12.5 }}>
        Tout dommage résultant d&rsquo;un usage manifestement abusif pourra faire
        l&rsquo;objet d&rsquo;une sanction disciplinaire.
      </p>
    </Card>
  </SectionWrap>
);

/* ============================================================ */
/* Router                                                        */
/* ============================================================ */

const TITLES: Record<string, string> = {
  accueil: 'Accueil',
  contacts: 'Contacts',
  pratiques: 'Pratiques',
  'produits-pl99': 'PL99-AL',
  'produits-defiv': 'DEFI-V.SID',
  reglement: 'Règlement',
  materiel: 'Matériel',
  convention: 'Convention',
  vehicule: 'Véhicule',
};

const RENDERERS: Record<string, FC> = {
  accueil: Accueil,
  contacts: Contacts,
  pratiques: Pratiques,
  'produits-pl99': ProduitsPL99,
  'produits-defiv': ProduitsDefiv,
  reglement: Reglement,
  materiel: Materiel,
  convention: Convention,
  vehicule: Vehicule,
};

export const LivretConsignes: FC = () => {
  const navbar = useNavbar();
  const navigate = useNavigate();
  const { section } = useParams<{ section: string }>();
  const key = section && RENDERERS[section] ? section : 'accueil';
  const { isRead } = useLivretRead(`consignes/${key}`);

  useEffect(() => {
    if (!navbar) {
      return;
    }
    navbar.setTitle(TITLES[key]);
    navbar.setBackArrow(true);
    navbar.setRight();
    navbar.setOnBack(() => {
      if (isRead) {
        navigate('/livret');
        return;
      }
      Dialog.confirm({
        title: '⚠ Lecture non validée',
        content:
          'Tu n’as pas encore validé la lecture de cette section. ' +
          'Reste sur la page et tape sur "✓ J’ai lu" en bas pour valider.',
        confirmText: 'Sortir quand même',
        cancelText: 'Rester sur la page',
      }).then((confirmed) => {
        if (confirmed) {
          faroLogger.warn('livret_section_exit_without_read', {
            section: `consignes/${key}`,
          });
          navigate('/livret');
        } else {
          faroLogger.info('livret_section_exit_cancelled', {
            section: `consignes/${key}`,
          });
        }
      });
    });
    faroLogger.info('livret_consignes_section_opened', { section: key });
  }, [navbar, navigate, key, isRead]);

  const Component = RENDERERS[key];
  return (
    <>
      <Component />
      <div style={{ padding: '0 14px 18px', maxWidth: 800, margin: '0 auto' }}>
        <ReadConfirm sectionKey={key} sectionTitle={TITLES[key]} />
      </div>
    </>
  );
};
